/** Guards /librarian/*: librarians and admins pass; everyone else is told why not. */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Shield, 
  ShieldAlert, 
  Loader2, 
  ArrowLeft, 
  Mail, 
  HelpCircle 
} from 'lucide-react';

interface LibrarianRouteGuardProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  fallbackRoute?: string;
  showContactInfo?: boolean;
}

const LibrarianRouteGuard: React.FC<LibrarianRouteGuardProps> = ({
  children,
  requiredPermissions = [],
  fallbackRoute = '/',
  showContactInfo = true
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Checking access
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Checking your permissions for the librarian dashboard...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redirect to signin if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate 
        to="/signin" 
        state={{ 
          from: location,
          message: 'Sign in to open the librarian dashboard'
        }} 
        replace 
      />
    );
  }

  // Check if user has librarian or admin role
  const hasLibrarianAccess = (): boolean => {
    if (!user) return false;
    
    const userType = user.user_type || user.role;
    const allowedRoles = ['librarian', 'admin'];
    
    // Check user_type field
    if (userType && allowedRoles.includes(userType.toLowerCase())) {
      return true;
    }
    
    // Check roles array if it exists
    if (Array.isArray(user.roles)) {
      return user.roles.some(role => 
        allowedRoles.includes(role.toLowerCase())
      );
    }
    
    // Check staff status as fallback
    if (user.is_staff || user.is_superuser) {
      return true;
    }
    
    return false;
  };

  // Check specific permissions if required
  const hasRequiredPermissions = (): boolean => {
    if (requiredPermissions.length === 0) return true;
    if (!user) return false;
    
    // requiredPermissions is not checked per permission: librarian access is the whole gate.
    return hasLibrarianAccess();
  };

  // Access denied - not a librarian
  if (!hasLibrarianAccess()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-red-100 dark:bg-red-900/20 rounded-full w-fit">
              <ShieldAlert className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">
              Access Restricted
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert variant="destructive">
              <Shield className="h-4 w-4" />
              <AlertDescription>
                The librarian dashboard requires librarian or administrator privileges. 
                Your current account ({user?.user_type || 'unknown'}) does not have the necessary permissions.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p className="font-medium mb-2">The dashboard opens for:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Librarian accounts</li>
                  <li>Administrator accounts</li>
                </ul>
              </div>
              
              {showContactInfo && (
                <Alert>
                  <Mail className="h-4 w-4" />
                  <AlertDescription>
                    If you should have access, ask the library manager or a system administrator 
                    to change your account.
                  </AlertDescription>
                </Alert>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                variant="outline" 
                onClick={() => window.history.back()}
                className="flex-1"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
              <Button 
                onClick={() => window.location.href = fallbackRoute}
                className="flex-1"
              >
                Return to Home
              </Button>
            </div>
            
            <div className="text-center">
              <Button variant="ghost" size="sm" asChild>
                <a href="/help" className="text-xs">
                  <HelpCircle className="h-3 w-3 mr-1" />
                  Need Help?
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Permission denied - insufficient permissions
  if (!hasRequiredPermissions()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-yellow-100 dark:bg-yellow-900/20 rounded-full w-fit">
              <Shield className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            <CardTitle className="text-2xl text-gray-900 dark:text-white">
              Insufficient Permissions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                You have librarian access, but this area also needs: 
                {requiredPermissions.join(', ')}
              </AlertDescription>
            </Alert>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                variant="outline" 
                onClick={() => window.history.back()}
                className="flex-1"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
              <Button 
                onClick={() => window.location.href = '/librarian'}
                className="flex-1"
              >
                Librarian Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // All checks passed - render protected content
  return <>{children}</>;
};

export default LibrarianRouteGuard;